import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useGenderPopup } from "../context/GenderPopupContext";

const sections = [
  { key: "about", id: "about" },
  { key: "coaching", id: "pricing" },
  { key: "results", id: "results" },
  { key: "content", id: "content" },
  { key: "blog", id: "blog" },
  { key: "contact", id: "contact" },
];

export default function Navbar() {
  const { t, locale, setLocale } = useI18n();
  const { setOpen: openGenderPopup } = useGenderPopup();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLink = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // Not on homepage — navigate to home with anchor
      window.location.href = `/#${id}`;
    }
    setOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: 1.4 }}
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
        <a
          href="#hero"
          onClick={handleLink("hero")}
          className="font-display text-2xl sm:text-3xl tracking-tight"
          data-testid="nav-logo"
        >
          MASTER<span style={{ color: "var(--mf-orange)" }}>FIT</span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {sections.map((s) => (
            <a
              key={s.key}
              href={`#${s.id}`}
              onClick={handleLink(s.id)}
              className="nav-link text-xs tracking-[0.25em] uppercase text-zinc-300 hover:text-white"
              data-testid={`nav-link-${s.key}`}
            >
              {t.nav[s.key]}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3 sm:gap-5">
          <div className="flex items-center text-xs tracking-widest" data-testid="lang-toggle">
            <button
              onClick={() => setLocale("es")}
              className={`px-2 py-1 transition ${
                locale === "es" ? "text-[#FF6B00] font-bold" : "text-zinc-500 hover:text-white"
              }`}
              data-testid="lang-es"
            >
              ES
            </button>
            <span className="text-zinc-700">|</span>
            <button
              onClick={() => setLocale("en")}
              className={`px-2 py-1 transition ${
                locale === "en" ? "text-[#FF6B00] font-bold" : "text-zinc-500 hover:text-white"
              }`}
              data-testid="lang-en"
            >
              EN
            </button>
          </div>

          <button
            onClick={() => openGenderPopup(true)}
            className="hidden md:inline-flex items-center px-5 py-3 bg-[#FF6B00] text-black font-bold text-xs tracking-[0.25em] btn-glow"
            data-testid="nav-cta-apply"
          >
            {t.nav.apply}
          </button>

          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-white"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
          data-testid="mobile-menu"
        >
          <div className="px-4 py-6 flex flex-col gap-5">
            {sections.map((s) => (
              <a
                key={s.key}
                href={`#${s.id}`}
                onClick={handleLink(s.id)}
                className="text-sm tracking-[0.25em] uppercase text-zinc-300"
                data-testid={`mobile-nav-${s.key}`}
              >
                {t.nav[s.key]}
              </a>
            ))}
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
}
